import { ArrowLeft } from 'lucide-react';
import { useEffect } from 'react';
import { href } from '../app/router';
import { GreekText } from '../components/GreekText';
import { PlayButton } from '../components/PlayButton';
import { getItem, staffOf } from '../content';
import { clipFor, itemVoice, speechRange } from '../lib/voice';
import { markReadingGuideSeen } from '../state/store';
import { InlineGreek } from '../components/InlineGreek';

const LETTERS: Array<{ upper: string; lower: string; sound: string; trap?: string }> = [
  { upper: 'Α', lower: 'α', sound: 'a, as in "father"' },
  { upper: 'Β', lower: 'β', sound: 'v', trap: 'Not b' },
  { upper: 'Γ', lower: 'γ', sound: 'a soft, throaty gh; y before e and i sounds', trap: 'Not g' },
  { upper: 'Δ', lower: 'δ', sound: 'dh: the th in "this"', trap: 'Not d' },
  { upper: 'Ε', lower: 'ε', sound: 'e, as in "bet"' },
  { upper: 'Ζ', lower: 'ζ', sound: 'z' },
  { upper: 'Η', lower: 'η', sound: 'ee', trap: 'Not h' },
  { upper: 'Θ', lower: 'θ', sound: 'th: the th in "thin"' },
  { upper: 'Ι', lower: 'ι', sound: 'ee' },
  { upper: 'Κ', lower: 'κ', sound: 'k' },
  { upper: 'Λ', lower: 'λ', sound: 'l' },
  { upper: 'Μ', lower: 'μ', sound: 'm' },
  { upper: 'Ν', lower: 'ν', sound: 'n', trap: 'Small ν is n, not v' },
  { upper: 'Ξ', lower: 'ξ', sound: 'ks' },
  { upper: 'Ο', lower: 'ο', sound: 'o' },
  { upper: 'Π', lower: 'π', sound: 'p' },
  { upper: 'Ρ', lower: 'ρ', sound: 'r, a light tap of the tongue', trap: 'Not p' },
  { upper: 'Σ', lower: 'σ ς', sound: 's (ς at the end of a word)' },
  { upper: 'Τ', lower: 'τ', sound: 't' },
  { upper: 'Υ', lower: 'υ', sound: 'ee', trap: 'Not u or y' },
  { upper: 'Φ', lower: 'φ', sound: 'f' },
  { upper: 'Χ', lower: 'χ', sound: 'ch as in Scottish "loch"; softer, like the h in "huge", before e and i sounds', trap: 'Not x' },
  { upper: 'Ψ', lower: 'ψ', sound: 'ps' },
  { upper: 'Ω', lower: 'ω', sound: 'o, the same as ο' },
];

const PAIRS: Array<{ pair: string; sound: string; example: string; itemId?: string }> = [
  { pair: 'ου', sound: 'oo', example: 'σουβλάκια', itemId: 'cafe-015' },
  { pair: 'αι', sound: 'e, as in "bet"', example: 'Είναι', itemId: 'cafe-017' },
  { pair: 'ει · οι', sound: 'ee', example: 'Μπορείτε', itemId: 'cafe-014' },
  { pair: 'μπ', sound: 'b', example: 'μπίρα', itemId: 'cafe-010' },
  { pair: 'ντ · γκ', sound: 'd · g', example: 'ντομάτα (tomato), γκαράζ (garage)' },
  { pair: 'γγ', sound: 'ng, as in "finger"', example: 'αγγλικά', itemId: 'cafe-004' },
  { pair: 'ευ · αυ', sound: 'ef/ev · af/av', example: 'Ευχαριστούμε', itemId: 'cafe-020' },
];

function Example({ itemId }: { itemId: string }) {
  const item = getItem(itemId);
  const clip = clipFor(item, itemVoice(item, staffOf(item)));
  const range = speechRange(clip);
  return <PlayButton src={clip.src} tag={`guide:${itemId}`} label="Hear it" ariaLabel={`Hear ${item.meaning}`} from={range.from} to={range.to} variant="icon" />;
}

/** A short reading primer: the letters that trick English speakers, the letter pairs, and stress. */
export function ReadingGuide() {
  useEffect(() => {
    markReadingGuideSeen();
  }, []);

  return (
    <main className="page" id="main">
      <a href={href('/')} className="btn btn-ghost" style={{ alignSelf: 'flex-start', marginLeft: -12 }}>
        <ArrowLeft size={18} aria-hidden="true" /> Today
      </a>
      <header className="stack">
        <p className="eyebrow">Reading guide</p>
        <h1 className="title">Reading Greek in three minutes</h1>
        <p className="subtitle">
          You'll learn the phrases by ear first, so reading can come slowly. These are the few things that trip English speakers up.
        </p>
      </header>

      <section className="stack-lg" aria-labelledby="stress-h">
        <h2 className="section-title" id="stress-h">
          1. The accent mark shows the stress
        </h2>
        <p>
          Every word of two or more syllables has a mark like <span className="greek" lang="el">ά</span> on the stressed vowel. In
          this app the whole stressed syllable is also <strong>bold and in colour</strong>. Stress changes meaning, so it matters:
        </p>
        <div className="panel row-wrap" style={{ gap: 24 }}>
          <div className="stack" style={{ gap: 2 }}>
            <GreekText text="πότε" size="large" />
            <span className="small muted">PO-te · when</span>
          </div>
          <div className="stack" style={{ gap: 2 }}>
            <GreekText text="ποτέ" size="large" />
            <span className="small muted">po-TE · never</span>
          </div>
        </div>
        <p className="small muted">
          And the Greek question mark is <span className="greek" lang="el">;</span>, which looks like an English semicolon.
        </p>
      </section>

      <section className="stack-lg" aria-labelledby="pairs-h">
        <h2 className="section-title" id="pairs-h">
          2. Letter pairs make one sound
        </h2>
        <ul className="phrase-list">
          {PAIRS.map((p) => (
            <li key={p.pair} className="phrase-row">
              <div className="phrase-link">
                <span>
                  <span className="greek greek-large" lang="el">
                    {p.pair}
                  </span>{' '}
                  <span className="muted">= {p.sound}</span>
                </span>
                <span className="small muted">
                  as in <InlineGreek text={p.example} />
                </span>
              </div>
              <div className="phrase-row-end">{p.itemId && <Example itemId={p.itemId} />}</div>
            </li>
          ))}
        </ul>
      </section>

      <section className="stack" aria-labelledby="respell-h">
        <h2 className="section-title" id="respell-h">
          3. The sounds-like spelling
        </h2>
        <p>
          Tap the Greek on a phrase card to see it in English letters, with the stressed syllable in capitals:{' '}
          <span className="greek" lang="el">
            Θα ήθελα έναν καφέ
          </span>{' '}
          → <strong>tha EE-theh-la EH-nan ka-FEH</strong>.
        </p>
        <ul className="stack small" style={{ paddingLeft: '1.1rem', margin: 0, gap: 4 }}>
          <li><strong>dh</strong> is the th in "this"; <strong>th</strong> is the th in "thin".</li>
          <li><strong>gh</strong> is a soft, throaty g; <strong>h</strong> or <strong>kh</strong> is the ch in Scottish "loch".</li>
          <li><strong>eh</strong> is the e in "bet"; <strong>ee</strong> as in "see"; <strong>oo</strong> as in "food".</li>
          <li>Greek vowels stay full even when not stressed: never "uh".</li>
        </ul>
      </section>

      <section className="stack-lg" aria-labelledby="letters-h">
        <div className="stack" style={{ gap: 4 }}>
          <h2 className="section-title" id="letters-h">
            4. The alphabet
          </h2>
          <p className="small muted">The letters with an outline look like English letters but sound different.</p>
        </div>
        <div className="alphabet">
          {LETTERS.map((l) => (
            <div key={l.upper} className={`letter ${l.trap ? 'letter-trap' : ''}`}>
              <span className="letter-glyph" lang="el">
                {l.upper} {l.lower}
              </span>
              <span className="small">{l.sound}</span>
              {l.trap && <span className="small" style={{ color: 'var(--terracotta)', fontWeight: 650 }}>{l.trap}</span>}
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}
